import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getUiErrorMessage, getUiErrorToastText } from '@/shared/api';
import {
  ApprovalRequestButton,
  Button,
  ConfirmDialog,
  FormField,
  InlineAlert,
  Input,
  Modal,
  ModalActions,
  Money,
  ReasonCodePicker,
  Spinner,
  Textarea,
  toast,
} from '@/shared/ui';

import {
  useCancelOrReturnOrderMutation,
  useOrderDetailQuery,
  type OrderPayoutDetails,
} from '../api/ordersApi';
import { canAdminCancel, canAdminReturn, needsCodCashPayout } from '../lib/orderCancelReturn';
import {
  createCancelOrReturnFormSchema,
  type CancelOrReturnFormValues,
} from '../schemas/cancelOrReturnSchema';
import type { OrderCancelReturnAction } from '../types';

const CANCEL_REASON_CODES = [
  'picker_unavailable',
  'stock_unavailable',
  'customer_requested',
  'duplicate_order',
  'delivery_not_possible',
  'other',
] as const;

const RETURN_REASON_CODES = [
  'customer_complaint',
  'damaged_or_wrong',
  'quality_issue',
  'customer_refused',
  'other',
] as const;

const EMPTY_FORM: CancelOrReturnFormValues = {
  reasonCode: '',
  reasonNote: '',
  accountHolderName: '',
  accountNumber: '',
  ifscCode: '',
  upiId: '',
};

export interface OrderCancelReturnDialogProps {
  open: boolean;
  orderId: string | null;
  action: OrderCancelReturnAction | null;
  onOpenChange: (open: boolean) => void;
}

export function OrderCancelReturnDialog({
  open,
  orderId,
  action,
  onOpenChange,
}: OrderCancelReturnDialogProps) {
  const { t } = useTranslation('orders');
  const { t: tc } = useTranslation('common');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const detailQuery = useOrderDetailQuery(open ? (orderId ?? undefined) : undefined);
  const detail = detailQuery.data;
  const mutation = useCancelOrReturnOrderMutation();

  const requirePayout = Boolean(detail && needsCodCashPayout(detail.payment));
  const requirePayoutRef = useRef(requirePayout);
  requirePayoutRef.current = requirePayout;
  const tRef = useRef(t);
  tRef.current = t;

  const form = useForm<CancelOrReturnFormValues>({
    resolver: async (values, context, options) =>
      zodResolver(createCancelOrReturnFormSchema(tRef.current, requirePayoutRef.current))(
        values,
        context,
        options,
      ),
    defaultValues: EMPTY_FORM,
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (!open) {
      form.reset(EMPTY_FORM);
      setConfirmOpen(false);
      setApprovalRequired(false);
      setSubmitError(null);
      return;
    }
    if (!detail) return;
    form.reset({
      ...EMPTY_FORM,
      accountHolderName: detail.customer.name === '—' ? '' : detail.customer.name,
    });
  }, [open, detail, form]);

  const reasonOptions = useMemo(() => {
    const codes = action === 'RETURN' ? RETURN_REASON_CODES : CANCEL_REASON_CODES;
    const prefix = action === 'RETURN' ? 'cancelReturn.reasons.return' : 'cancelReturn.reasons.cancel';
    return codes.map((code) => ({
      value: code,
      label: t(`${prefix}.${code}` as 'cancelReturn.reasons.cancel.other'),
    }));
  }, [action, t]);

  const close = () => onOpenChange(false);

  const resolveReason = (values: CancelOrReturnFormValues): string => {
    if (values.reasonCode === 'other') return values.reasonNote.trim();
    const prefix = action === 'RETURN' ? 'cancelReturn.reasons.return' : 'cancelReturn.reasons.cancel';
    return t(`${prefix}.${values.reasonCode}` as 'cancelReturn.reasons.cancel.other');
  };

  const buildPayout = (values: CancelOrReturnFormValues): OrderPayoutDetails | undefined => {
    if (!requirePayout) return undefined;
    return {
      accountHolderName: values.accountHolderName.trim(),
      accountNumber: values.accountNumber.trim(),
      ifscCode: values.ifscCode.trim().toUpperCase(),
      upiId: values.upiId.trim(),
    };
  };

  const onRequestConfirm = form.handleSubmit(() => {
    if (!detail || !action) return;
    if (action === 'CANCEL' && !canAdminCancel(detail.status)) {
      setSubmitError(t('cancelReturn.errors.cancelNotAllowed'));
      return;
    }
    if (action === 'RETURN' && !canAdminReturn(detail)) {
      setSubmitError(t('cancelReturn.errors.returnNotAllowed'));
      return;
    }
    setSubmitError(null);
    setConfirmOpen(true);
  });

  const onConfirm = async () => {
    if (!detail || !action || !orderId) return;
    const values = form.getValues();
    setSubmitError(null);
    try {
      const result = await mutation.mutateAsync({
        orderId,
        action,
        reason: resolveReason(values),
        payout: buildPayout(values),
      });
      if (result.approvalRequired) {
        setApprovalRequired(true);
        setConfirmOpen(false);
        toast.success({
          title: t('cancelReturn.toasts.approvalTitle'),
          description: result.message || t('cancelReturn.toasts.approvalDescription'),
        });
        return;
      }
      toast.success({
        title:
          result.message ||
          (action === 'RETURN'
            ? t('cancelReturn.toasts.returnedTitle')
            : t('cancelReturn.toasts.cancelledTitle')),
        description: t('cancelReturn.toasts.successDescription', {
          orderNumber: detail.orderNumber,
        }),
      });
      setConfirmOpen(false);
      close();
    } catch (err) {
      setConfirmOpen(false);
      const ui = getUiErrorToastText(tc, err);
      setSubmitError(getUiErrorMessage(tc, err));
      toast.error({ title: ui.title, description: ui.description });
    }
  };

  const isReturn = action === 'RETURN';
  const collectedPaise = detail?.payment.collectedAmountPaise;
  const showCollected =
    typeof collectedPaise === 'number' && Number.isInteger(collectedPaise) && collectedPaise > 0;

  return (
    <>
      <Modal
        open={open}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        title={isReturn ? t('cancelReturn.returnTitle') : t('cancelReturn.cancelTitle')}
        description={
          detail
            ? t(isReturn ? 'cancelReturn.returnDescription' : 'cancelReturn.cancelDescription', {
                orderNumber: detail.orderNumber,
                customer: detail.customer.name,
              })
            : t('cancelReturn.loadingDescription')
        }
        showClose
        size="md"
        footer={
          <ModalActions>
            <Button type="button" variant="secondary" onClick={close} disabled={mutation.isPending}>
              {tc('actions.cancel')}
            </Button>
            {approvalRequired ? (
              <ApprovalRequestButton disabled />
            ) : (
              <Button
                type="submit"
                form="order-cancel-return-form"
                variant="danger"
                disabled={!detail || mutation.isPending || detailQuery.isLoading}
              >
                {isReturn ? t('cancelReturn.submitReturn') : t('cancelReturn.submitCancel')}
              </Button>
            )}
          </ModalActions>
        }
      >
        {detailQuery.isLoading && !detail ? (
          <div className="flex justify-center py-8">
            <Spinner className="text-primary" size={28} label={tc('state.loading')} />
          </div>
        ) : detailQuery.isError || !detail ? (
          <InlineAlert>{t('cancelReturn.errors.loadFailed')}</InlineAlert>
        ) : (
          <form
            id="order-cancel-return-form"
            className="flex flex-col gap-4"
            onSubmit={(e) => void onRequestConfirm(e)}
          >
            <ReasonCodePicker
              name="reasonCode"
              required
              value={form.watch('reasonCode')}
              onChange={(value) => form.setValue('reasonCode', value, { shouldValidate: true })}
              options={reasonOptions}
            />
            {form.formState.errors.reasonCode?.message ? (
              <p role="alert" className="-mt-2 text-xs text-danger-strong">
                {form.formState.errors.reasonCode.message}
              </p>
            ) : null}

            {form.watch('reasonCode') === 'other' ? (
              <FormField error={form.formState.errors.reasonNote?.message} required>
                <FormField.Label required>{t('cancelReturn.otherReasonLabel')}</FormField.Label>
                <FormField.Control>
                  <Textarea
                    rows={3}
                    value={form.watch('reasonNote')}
                    onChange={(e) => form.setValue('reasonNote', e.target.value, { shouldValidate: true })}
                    placeholder={t('cancelReturn.otherReasonPlaceholder')}
                  />
                </FormField.Control>
              </FormField>
            ) : null}

            {requirePayout ? (
              <div className="flex flex-col gap-4 rounded-xl border border-border bg-surface-muted/40 p-4">
                <div>
                  <h3 className="text-sm font-semibold text-fg">{t('cancelReturn.payout.heading')}</h3>
                  <p className="mt-1 text-xs text-fg-muted">{t('cancelReturn.payout.hint')}</p>
                  {showCollected ? (
                    <p className="mt-2 text-sm font-medium text-fg">
                      {t('cancelReturn.payout.collectedLabel')}{' '}
                      <Money amount={collectedPaise} />
                    </p>
                  ) : null}
                </div>

                <FormField error={form.formState.errors.accountHolderName?.message} required>
                  <FormField.Label required>{t('cancelReturn.payout.accountHolder')}</FormField.Label>
                  <FormField.Control>
                    <Input
                      autoComplete="name"
                      value={form.watch('accountHolderName')}
                      onChange={(e) =>
                        form.setValue('accountHolderName', e.target.value, { shouldValidate: true })
                      }
                    />
                  </FormField.Control>
                </FormField>

                <FormField error={form.formState.errors.accountNumber?.message}>
                  <FormField.Label>{t('cancelReturn.payout.accountNumber')}</FormField.Label>
                  <FormField.Control>
                    <Input
                      inputMode="numeric"
                      autoComplete="off"
                      value={form.watch('accountNumber')}
                      onChange={(e) =>
                        form.setValue('accountNumber', e.target.value.replace(/\D/g, ''), {
                          shouldValidate: true,
                        })
                      }
                    />
                  </FormField.Control>
                </FormField>

                <FormField error={form.formState.errors.ifscCode?.message}>
                  <FormField.Label>{t('cancelReturn.payout.ifsc')}</FormField.Label>
                  <FormField.Control>
                    <Input
                      autoComplete="off"
                      className="uppercase"
                      value={form.watch('ifscCode')}
                      onChange={(e) =>
                        form.setValue('ifscCode', e.target.value.toUpperCase(), { shouldValidate: true })
                      }
                    />
                  </FormField.Control>
                </FormField>

                <FormField error={form.formState.errors.upiId?.message}>
                  <FormField.Label>{t('cancelReturn.payout.upi')}</FormField.Label>
                  <FormField.Control>
                    <Input
                      autoComplete="off"
                      placeholder={t('cancelReturn.payout.upiPlaceholder')}
                      value={form.watch('upiId')}
                      onChange={(e) => form.setValue('upiId', e.target.value, { shouldValidate: true })}
                    />
                  </FormField.Control>
                </FormField>
              </div>
            ) : (
              <p className="text-xs text-fg-muted">{t('cancelReturn.autoRefundHint')}</p>
            )}

            {submitError ? <InlineAlert>{submitError}</InlineAlert> : null}
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={isReturn ? t('cancelReturn.confirm.returnTitle') : t('cancelReturn.confirm.cancelTitle')}
        description={
          detail
            ? t(
                isReturn
                  ? 'cancelReturn.confirm.returnDescription'
                  : 'cancelReturn.confirm.cancelDescription',
                { orderNumber: detail.orderNumber },
              )
            : undefined
        }
        confirmLabel={
          isReturn ? t('cancelReturn.confirm.returnConfirm') : t('cancelReturn.confirm.cancelConfirm')
        }
        confirmVariant="danger"
        isConfirming={mutation.isPending}
        onConfirm={() => void onConfirm()}
      />
    </>
  );
}
